import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { HeaderComponent } from './layout/header/header.component';
import { StaffPortalPreviewBarComponent } from './layout/staff-portal-preview-bar/staff-portal-preview-bar.component';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { FooterComponent } from './layout/footer/footer.component';
import { LoadingOverlayComponent } from './core/components/loading-overlay/loading-overlay.component';
import { ConfirmationPopupComponent } from './core/components/confirmation-popup/confirmation-popup.component';
import { ToastModule } from 'primeng/toast';
import { APP_CONSTANTS } from './core/constants/app.constants';
import { LoadingService } from './core/Provider/loading.service';
import { filter, merge, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { VersionCheckService } from './core/services/version-check.service';
import { getStoredUserData, getStoredUserId, getStoredUserRole } from './core/utils/user-storage.utils';
import {
  CSE_NOTE_REMINDER_TOAST_KEY,
  CSE_NOTE_REMINDER_TOAST_LIFE_MS,
  cseNoteReminderCheck$,
  cseNoteReminderOpen$,
  isCseNoteReminderUser,
  msUntilCseNoteReminder,
  readCseNoteReminderShownAt,
  writeCseNoteReminderShownAt
} from './layout/header/cse-note-reminder';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    StaffPortalPreviewBarComponent,
    SidebarComponent,
    FooterComponent,
    LoadingOverlayComponent,
    LoadingOverlayComponent,
    ConfirmationPopupComponent,
    ToastModule
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  private router = inject(Router);
  private loadingService = inject(LoadingService);
  private versionCheckService = inject(VersionCheckService);
  private messageService = inject(MessageService);
  private destroy$ = new Subject<void>();
  private cseReminderTimer: ReturnType<typeof setTimeout> | null = null;
  private cseReminderUserId = '';

  readonly cseNoteReminderToastKey = CSE_NOTE_REMINDER_TOAST_KEY;
  
  protected readonly title = 'ElevateMe CRM';
  isLoading = false;

  get showLayout(): boolean {
    // Show layout only after login success. Never show on initial/empty url or public routes (avoids sidebar flash).
    const url = this.router.url;
    if (!url || url === '/' || url === '') return false;
    const isAuthRoute = url.includes('/auth/');
    const isStudentOnboarding =
      url.includes(APP_CONSTANTS.ROUTES.STUDENT_ONBOARDING) ||
      url.includes(APP_CONSTANTS.ROUTES.STUDENT_ONBOARDING_DRAFT_SUCCESS) ||
      url.includes(APP_CONSTANTS.ROUTES.STUDENT_ONBOARDING_SUBMIT_SUCCESS);
    const isPublicRoute = [
      APP_CONSTANTS.ROUTES.PRIVACY_POLICY,
      APP_CONSTANTS.ROUTES.CSA_LEADERBOARD,
      APP_CONSTANTS.ROUTES.CSE_LEADERBOARD,
      APP_CONSTANTS.ROUTES.LEADERBOARD
    ].some((route) => url.includes(route));
    return !isAuthRoute && !isStudentOnboarding && !isPublicRoute;
  }

  get showSidebar(): boolean {
    // Sidebar follows showLayout; hide for auth routes and student dashboard
    const url = this.router.url;
    if (!this.showLayout) return false;
    return !url.includes(APP_CONSTANTS.ROUTES.STUDENT_DASHBOARD);
  }

  ngOnInit(): void {
    console.log('Current Environment:', environment.name);
    this.versionCheckService.start();

    this.loadingService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        this.isLoading = loading;
      });

    merge(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)),
      cseNoteReminderCheck$
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncCseNoteReminder());
  }

  openCseNoteReminder(event: Event): void {
    event.stopPropagation();
    this.messageService.clear(CSE_NOTE_REMINDER_TOAST_KEY);
    cseNoteReminderOpen$.next();
  }

  ngOnDestroy(): void {
    this.clearCseNoteReminder();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private syncCseNoteReminder(): void {
    const userId = getStoredUserId() || 'cse';
    if (!this.shouldShowCseNoteReminder()) {
      this.clearCseNoteReminder();
      return;
    }
    if (this.cseReminderUserId === userId && this.cseReminderTimer !== null) {
      return;
    }
    this.cseReminderUserId = userId;
    this.armCseNoteReminder();
  }

  private shouldShowCseNoteReminder(): boolean {
    if (!this.showLayout) {
      return false;
    }
    const stored = getStoredUserData();
    const displayName = stored ? String(stored['roleDisplayName'] ?? stored['RoleDisplayName'] ?? '') : '';
    return isCseNoteReminderUser(getStoredUserRole(), displayName);
  }

  private armCseNoteReminder(): void {
    if (this.cseReminderTimer !== null) {
      clearTimeout(this.cseReminderTimer);
    }
    const wait = msUntilCseNoteReminder(readCseNoteReminderShownAt(this.cseReminderUserId), Date.now());
    this.cseReminderTimer = setTimeout(() => this.showCseNoteReminder(), wait);
  }

  private showCseNoteReminder(): void {
    this.cseReminderTimer = null;
    if (!this.shouldShowCseNoteReminder()) {
      this.clearCseNoteReminder();
      return;
    }
    writeCseNoteReminderShownAt(this.cseReminderUserId);
    this.messageService.clear(CSE_NOTE_REMINDER_TOAST_KEY);
    this.messageService.add({
      key: CSE_NOTE_REMINDER_TOAST_KEY,
      severity: 'info',
      summary: 'Spoke with a student?',
      detail: 'Add a note now so the whole team stays aligned.',
      life: CSE_NOTE_REMINDER_TOAST_LIFE_MS
    });
    this.armCseNoteReminder();
  }

  private clearCseNoteReminder(): void {
    if (this.cseReminderTimer !== null) {
      clearTimeout(this.cseReminderTimer);
      this.cseReminderTimer = null;
    }
    this.cseReminderUserId = '';
    this.messageService.clear(CSE_NOTE_REMINDER_TOAST_KEY);
  }
}
